import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiPackage, FiTruck, FiCheck, FiMapPin, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../Components/LoadingSpinner';

// Shared product cache with OrderHistory
const productCache = {};

const OrderTracking = () => {
  const { orderId } = useParams();
  const { token } = useSelector((state) => state.user);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productDetails, setProductDetails] = useState({});

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(`${API_BASE_URL}/order/${orderId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('Order details response:', response);
        
        if (response.status === 200 && response.data && response.data.data) {
          console.log('Order details:', response.data.data);
          setOrder(response.data.data);
          
          // Get product IDs to fetch details
          const productIds = new Set();
          if (response.data.data.cartItems && response.data.data.cartItems.length > 0) {
            response.data.data.cartItems.forEach(item => {
              if (item.product && item.product._id) {
                productIds.add(item.product._id);
              }
            });
            
            // Fetch product details
            await fetchProductDetails(Array.from(productIds));
          }
        } else {
          console.error('Order details response not successful:', response.data);
          setError('Failed to retrieve order details. Please try again.');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err.response?.data?.message || 'Failed to fetch order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    // Function to fetch product details
    const fetchProductDetails = async (productIds) => {
      try {
        const productData = {};
        
        // Check cache first
        const idsToFetch = productIds.filter(id => !productCache[id]);
        
        // Fetch only uncached products
        if (idsToFetch.length > 0) {
          await Promise.all(
            idsToFetch.map(async (productId) => {
              try {
                const response = await axios.get(`${API_BASE_URL}/products/${productId}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (response.data && response.data.data) {
                  productCache[productId] = response.data.data;
                  productData[productId] = response.data.data;
                }
              } catch (error) {
                console.error(`Failed to fetch product ${productId}:`, error);
              }
            })
          );
        }
        
        // Add cached products
        productIds.forEach(id => {
          if (productCache[id] && !productData[id]) {
            productData[id] = productCache[id];
          }
        });
        
        setProductDetails(prev => ({ ...prev, ...productData }));
      } catch (error) {
        console.error('Error fetching product details:', error);
      }
    };

    if (token && orderId) {
      fetchOrder();
    }
  }, [token, orderId]);
  
  // Helper function to get product details
  const getProductDetail = (productId) => {
    return productDetails[productId] || {};
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen bg-cream flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white p-6 rounded-2xl border border-sand shadow-card">
              <div className="flex items-center justify-center text-red-500 mb-4">
                <FiAlertCircle className="w-8 h-8" />
              </div>
              <p className="text-center text-lg">{error || 'Order not found'}</p>
              <div className="mt-6 text-center">
                <Link 
                  to="/orders"
                  className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-2 rounded-xl font-medium hover:bg-brand-500 hover:text-ink transition-colors"
                >
                  <FiArrowLeft /> Back to Orders
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Create timeline based on order status
  const orderTimeline = [
    {
      status: 'ordered',
      description: 'Your order has been placed',
      date: order.createdAt,
      completed: true
    },
    {
      status: 'processing',
      description: 'Your order is being processed',
      date: order.isPaid ? order.paidAt : null,
      completed: order.isPaid
    },
    {
      status: 'delivered',
      description: 'Your order has been delivered',
      date: order.isDelivered ? order.deliveredAt : null,
      completed: order.isDelivered
    }
  ];

  return (
    <>
      <Navbar />
      <div className="pt-24 min-h-screen bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/orders" className="text-brand-600 hover:text-brand-700">
              <FiArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-display font-normal tracking-tight text-gray-900">Order Tracking</h1>
          </div>
          
          <div className="space-y-6">
            {/* Order Status */}
            <div className="bg-white rounded-2xl border border-sand shadow-card p-6">
              <h2 className="text-lg font-display font-normal tracking-tight text-gray-900 mb-4">Order #{order._id.substring(0, 8)}...</h2>
              <div className="space-y-8">
                {orderTimeline.map((event, index) => (
                  <div key={index} className="relative flex items-start">
                    {index < orderTimeline.length - 1 && (
                      <div className="absolute top-10 left-5 w-0.5 h-full bg-gray-200"></div>
                    )}
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center z-10
                      ${event.completed ? 'bg-green-600' : 'bg-gray-300'}
                      text-white
                    `}>
                      {event.status === 'ordered' && <FiPackage className="w-5 h-5" />}
                      {event.status === 'processing' && <FiTruck className="w-5 h-5" />}
                      {event.status === 'delivered' && <FiCheck className="w-5 h-5" />}
                    </div>
                    <div className="ml-4">
                      <p className="text-md font-medium text-gray-900 capitalize">{event.status}</p>
                      <p className="text-sm text-gray-500">{event.description}</p>
                      {event.date && (
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(event.date).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Details */}
            <div className="bg-white rounded-2xl border border-sand shadow-card p-6">
              <h2 className="text-lg font-display font-normal tracking-tight text-gray-900 mb-4">Order Details</h2>
              <div className="space-y-4">
                {order.cartItems.map((item) => {
                  const productId = item.product?._id;
                  const product = getProductDetail(productId);
                  
                  return (
                    <div key={item._id} className="flex items-center py-3 border-b border-gray-100 last:border-0">
                      <img
                        src={product?.images?.[0] || 'https://via.placeholder.com/150'}
                        alt={product?.name || 'Product'}
                        className="w-16 h-16 object-cover rounded"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/150';
                        }}
                      />
                      <div className="ml-4 flex-1">
                        <p className="font-medium">{product?.name || 'Product'}</p>
                        <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                        {item.leftEyePower && item.rightEyePower && (
                          <div className="text-gray-500 text-xs mt-1">
                            <span>Left: {item.leftEyePower} | Right: {item.rightEyePower}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">EGP {(product?.price || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
                
                <div className="border-t border-sand pt-4 mt-4">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>EGP {(order.totalOrderPrice - (order.shippingPrice || 0)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span>Shipping</span>
                    <span>EGP {(order.shippingPrice || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-display font-normal tracking-tight mt-3">
                    <span>Total</span>
                    <span>EGP {order.totalOrderPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-2xl border border-sand shadow-card p-6">
              <h2 className="text-lg font-display font-normal tracking-tight text-gray-900 mb-4">Payment Information</h2>
              <div className="flex items-center space-x-2 mb-3">
                <div className={`w-3 h-3 rounded-full ${order.isPaid ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <span className="font-medium">{order.isPaid ? 'Paid' : 'Payment Pending'}</span>
              </div>
              <p className="text-gray-600">Method: {order.paymentMethodType === 'card' ? 'Credit Card' : 'Cash on Delivery'}</p>
              {order.isPaid && order.paidAt && (
                <p className="text-gray-600 mt-1">
                  Paid on: {new Date(order.paidAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>

            {/* Delivery Address */}
            <div className="bg-white rounded-2xl border border-sand shadow-card p-6">
              <h2 className="text-lg font-display font-normal tracking-tight text-gray-900 mb-4">Delivery Address</h2>
              <div className="flex items-start space-x-4">
                <FiMapPin className="w-5 h-5 text-gray-400 mt-1" />
                <div>
                  <p className="font-medium">{order.user?.name}</p>
                  <p className="text-gray-600">{order.shippingAddress?.details}</p>
                  <p className="text-gray-600">{order.shippingAddress?.city}</p>
                  <p className="text-gray-600">
                    {order.shippingAddress?.postalCode && `Postal Code: ${order.shippingAddress.postalCode}`}
                  </p>
                  <p className="text-gray-600">Phone: {order.shippingAddress?.phone}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderTracking; 